import { useState, useMemo } from 'react';
import { B } from '../../constants/brand';
import { folderPathError, normalizeFolderPath, FOLDER_SOURCE_LABEL } from '../../utils/docFolders';
import { sanitizeNumericInput, WHOLE } from '../../utils/numericInput';
import { Icon } from '../ui';

// FolderPicker — the destination-folder tree for a document upload.
//
// Renders the DYNAMIC folder list (see useDocumentFolders): the folders Claude
// generated for this deal from its documents, the ones someone added in Harvi,
// the ones that actually exist under the deal in SharePoint, and the ones its
// documents already use — all in one tree, each tagged with where it came from.
// Any depth renders, because the structure is never known to the app in
// advance: a segment nobody declared as a destination is a group header,
// everything else is pickable.
//
// "＋ New folder" creates the folder in SharePoint as well, so a user who needs
// somewhere new to file something doesn't have to leave for SharePoint and come
// back. Pass `onCreateFolder` to enable it.

const SOURCE_CHIP = {
  ai:         { bg: '#eef2f7', fg: B.textMd },
  custom:     { bg: B.tealLt,  fg: B.navyDk },
  sharepoint: { bg: '#eaf3ea', fg: '#1d6f42' },
  documents:  { bg: '#f4f0e8', fg: '#8a6d1f' },
};

const SourceChip = ({ source }) => {
  if (!source) return null;
  const c = SOURCE_CHIP[source] || SOURCE_CHIP.ai;
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8,
      background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {FOLDER_SOURCE_LABEL[source] || source}
    </span>
  );
};

const TreeNode = ({ node, depth, selected, onSelect }) => {
  const sel = node.selectable && selected === node.path;
  const pad = 10 + depth * 14;

  if (!node.selectable) {
    return (
      <div>
        <div style={{ background: depth === 0 ? B.navy : B.surfaceMuted,
          color: depth === 0 ? '#fff' : B.textMd,
          fontSize: 11, fontWeight: 700, padding: `6px 10px 6px ${pad}px`, letterSpacing: 0.3 }}>
          {node.label || node.name}
        </div>
        {node.children.map(child => (
          <TreeNode key={child.path} node={child} depth={depth + 1} selected={selected} onSelect={onSelect} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div
        onClick={() => onSelect(node.path, node)}
        title={node.hint || node.path}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: `7px 10px 7px ${pad}px`, fontSize: 13, cursor: 'pointer',
          borderBottom: `1px solid ${B.border}`,
          background: sel ? B.tealLt : B.white, color: sel ? B.navyDk : B.text,
          fontWeight: sel ? 600 : 400,
          borderLeft: `3px solid ${sel ? B.tealDk : 'transparent'}` }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ display: 'flex', color: sel ? B.tealDk : B.textLt, flexShrink: 0 }}>
            <Icon name="folder" size={13} />
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.label || node.name}
          </span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {node.docCount > 0 && (
            <span style={{ fontSize: 10, color: B.textLt }}>{node.docCount}</span>
          )}
          <SourceChip source={node.source} />
        </span>
      </div>
      {node.children.map(child => (
        <TreeNode key={child.path} node={child} depth={depth + 1} selected={selected} onSelect={onSelect} />
      ))}
    </div>
  );
};

export const FolderPicker = ({
  tree = [], folders = [], loading = false, error = null,
  selected, onSelect,
  taxYear, onTaxYear,
  onCreateFolder,               // async (path) => folder — omit to hide "New folder"
  sharePointAvailable = false,
  // No asterisk: picking a folder is optional everywhere this is used —
  // an upload left unset is auto-filed server-side (documentAutoFile.js).
  label = 'Folder',
  // Shown under the label. Callers that auto-file say so here.
  hint = null,
  emptyHint = 'No folders yet — a structure is designed from this deal’s documents as you upload. Add one here to choose your own.',
}) => {
  const [adding, setAdding]   = useState(false);
  const [newPath, setNewPath] = useState('');
  const [busy, setBusy]       = useState(false);
  const [addErr, setAddErr]   = useState(null);

  const selectedFolder = useMemo(
    () => folders.find(f => f.path === selected) || null,
    [folders, selected]
  );
  const requiresYear = !!selectedFolder?.requiresYear;

  // A new folder is created relative to the selection, so "New folder" while
  // "Legal" is highlighted makes a sub-folder of Legal — the thing people
  // actually mean, without typing the whole path.
  const parentPath = selected || '';

  const submitNew = async () => {
    const name = newPath.trim();
    const full = parentPath ? `${parentPath}/${name}` : name;
    const err = folderPathError(full);
    if (err) { setAddErr(err); return; }
    setBusy(true); setAddErr(null);
    try {
      const created = await onCreateFolder(normalizeFolderPath(full));
      setAdding(false); setNewPath('');
      if (created?.path) onSelect(created.path, created);
    } catch (e) {
      setAddErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {label}
        </div>
        {onCreateFolder && !adding && (
          <span onClick={() => { setAdding(true); setAddErr(null); }}
            style={{ fontSize: 12, fontWeight: 600, color: B.tealDk, cursor: 'pointer' }}>
            + New folder
          </span>
        )}
      </div>

      {hint && (
        <div style={{ fontSize: 11.5, color: B.textLt, marginBottom: 7, lineHeight: 1.5 }}>{hint}</div>
      )}

      {adding && (
        <div style={{ border: `1px solid ${B.tealDk}`, borderRadius: 6, padding: 10, marginBottom: 8, background: B.surfaceMuted }}>
          <div style={{ fontSize: 11, color: B.textMd, marginBottom: 6 }}>
            {parentPath ? <>New folder inside <strong>{parentPath}</strong></> : 'New folder at the top level of this deal'}
            {sharePointAvailable && ' — it will be created in SharePoint too.'}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input autoFocus value={newPath} disabled={busy}
              onChange={e => { setNewPath(e.target.value); setAddErr(null); }}
              onKeyDown={e => { if (e.key === 'Enter') submitNew(); if (e.key === 'Escape') setAdding(false); }}
              placeholder="Folder name"
              style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: `1px solid ${B.border}`,
                fontSize: 13, fontFamily: 'inherit', background: B.white, color: B.text, outline: 'none' }} />
            <button type="button" onClick={submitNew} disabled={busy || !newPath.trim()}
              style={{ padding: '7px 12px', borderRadius: 6, border: 'none', background: B.tealDk, color: '#fff',
                fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy || !newPath.trim() ? 0.6 : 1 }}>
              {busy ? 'Creating…' : 'Create'}
            </button>
            <button type="button" onClick={() => { setAdding(false); setNewPath(''); setAddErr(null); }} disabled={busy}
              style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${B.border}`, background: B.white,
                color: B.textMd, fontSize: 12, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
          {addErr && <div style={{ fontSize: 11, color: B.red, marginTop: 6 }}>{addErr}</div>}
        </div>
      )}

      <div style={{ border: `1px solid ${B.border}`, borderRadius: 6, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
        {loading && (
          <div style={{ padding: 14, fontSize: 12, color: B.textLt }}>Loading folders…</div>
        )}
        {!loading && tree.length === 0 && (
          <div style={{ padding: 14, fontSize: 12, color: B.textLt }}>{emptyHint}</div>
        )}
        {!loading && tree.map(node => (
          <TreeNode key={node.path} node={node} depth={0} selected={selected} onSelect={onSelect} />
        ))}
      </div>

      {error && (
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 5 }}>
          Showing the default folders — the live folder list couldn’t be loaded ({error}).
        </div>
      )}

      {selected && (
        <div style={{ marginTop: 5, padding: '4px 10px', borderRadius: 4, fontSize: 11, display: 'inline-flex',
          alignItems: 'center', gap: 5, background: B.tealLt, color: B.navyDk }}>
          <Icon name="folder" size={13} />
          <span style={{ fontWeight: 600 }}>{selected}{taxYear ? `/${taxYear}` : ''}</span>
        </div>
      )}

      {/* Digits only, and that is load-bearing rather than tidy: this value
          becomes a folder path SEGMENT (`folder/taxYear`), so a lone "." would
          be a folder named "." in SharePoint. A number input did discard that
          one (measured), so the plain-text replacement has to as well. */}
      {requiresYear && (
        <input type="text" inputMode="numeric" placeholder={`Tax Year (e.g. ${new Date().getFullYear()})`}
          value={taxYear || ''} onChange={e => onTaxYear(sanitizeNumericInput(e.target.value, WHOLE))}
          style={{ display: 'block', width: '100%', marginTop: 6, padding: '7px 10px', borderRadius: 6,
            border: `1px solid ${B.border}`, fontSize: 13, fontFamily: 'inherit',
            background: B.white, color: B.text, outline: 'none', boxSizing: 'border-box' }} />
      )}
    </div>
  );
};
